import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import { API_HOST } from 'utils'
import { getImageUrl, toAltTitle } from 'utils/safeTs'
import styles from './WImage.module.sass'

interface Props {
  next?: boolean // Se utilizzare next o meno, default true
  src?: string
  alt?: string
  title?: string
  className?: string
  maxWidth: number
  maxHeight: number
  blur?: boolean
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down'
  priority?: false | true
  loading?: 'lazy' | 'eager'
  fallback?: string // object
  editMode?: boolean
  onLoad?: (e: React.ChangeEvent<HTMLImageElement>) => void
  style?: any
  onClick?: () => void
  quality?: number
  mask?: string
}

const getFinalUrl = (url: string) => {
  // se è relativo, aggiungo dominio corrente
  if (process.env.NODE_ENV === 'development' && url?.startsWith('/')) {
    return url.includes('/d/') ? 'http://localhost:9999' + url : 'http://localhost:3000' + url
  }

  if (url?.startsWith('/')) {
    return API_HOST + url
  }

  return url
}

const WImage = (props: Props) => {
  const {
    next = true,
    src = '',
    alt = '',
    title = '',
    maxWidth = 0,
    maxHeight = 0,
    className = '',
    objectFit = undefined,
    blur = false,
    priority = false,
    loading = 'lazy',
    fallback = undefined,
    onLoad = undefined,
    style = undefined,
    onClick = undefined,
    quality = 75,
    mask = undefined,
  } = props

  const [imgSrc, setImgSrc] = useState(getFinalUrl(src))
  const fallbackImage = fallback || `https://placehold.co/${maxWidth}x${maxHeight}/png`
  const finalStyle = mask
    ? { ...(style || {}), maskImage: `url(${getImageUrl(`masks/${mask}.svg`)})` }
    : style

  // dal pagebuilder src può cambiare, va tenuto aggiornato
  useEffect(() => {
    const getPagebuilderExportUrl = async (src: string) => {
      let finalUrl = src
      if (src.includes('/filer/canonical/')) {
        try {
          await fetch(API_HOST + src).then((response) => {
            if (response.redirected) finalUrl = response.url
          })
        } catch (e) {
          console.error(e)
        }
      }
      return finalUrl
    }

    getPagebuilderExportUrl(src).then((url) => setImgSrc(getFinalUrl(url)))
  }, [src])

  return next ? (
    <Image
      src={imgSrc || fallbackImage}
      alt={toAltTitle(alt)}
      // title={toAltTitle(title)} // il cliente chiede di rimuovere il tooltip dalle immagini - centralizziamo qui
      width={maxWidth}
      height={maxHeight}
      priority={priority}
      loading={loading}
      placeholder={blur ? 'blur' : undefined}
      blurDataURL={blur ? `/_next/image?url=${imgSrc || fallbackImage}&w=32&q=70` : undefined}
      onLoad={onLoad}
      onError={() => {
        setImgSrc(fallbackImage)
      }}
      className={`${styles.root} ${
        objectFit ? styles[`root--objectFit-${objectFit}`] : ''
      } ${className}`}
      style={finalStyle}
      onClick={onClick}
      quality={quality}
    />
  ) : (
    <img
      src={imgSrc || fallbackImage}
      alt={toAltTitle(alt)}
      // title={toAltTitle(title)} // il cliente chiede di rimuovere il tooltip dalle immagini - centralizziamo qui
      className={className}
      onLoad={onLoad}
      style={finalStyle}
      onClick={onClick}
    />
  )
}

export default WImage
